'use client'

import { ToolRankingChart } from '@excellent/ui/blocks/lens/tools/tool-ranking-chart'
import { McpServerPanel } from '@excellent/ui/blocks/lens/tools/mcp-server-panel'
import { FeatureAdoptionTable } from '@excellent/ui/blocks/lens/tools/feature-adoption-table'
import { VersionHistoryTable } from '@excellent/ui/blocks/lens/tools/version-history-table'
import { CATEGORY_COLORS, CATEGORY_LABELS } from '@lens/lib/tool-categories'
import { Card, CardContent } from "@excellent/ui"
import { SectionTitle } from "@excellent/ui"
import { StatStrip, type StatStripItem } from "@excellent/ui"
import { Skeleton } from "@excellent/ui"
import { Alert, AlertDescription } from "@excellent/ui"
import { Badge } from "@excellent/ui"
import { AlertTriangle } from "@excellent/ui/icons"
import { useSnapshot } from '@lens/lib/use-snapshot'

export default function ToolsPage() {
 const { snapshot, error, isLoading } = useSnapshot()
 const data = snapshot?.tools
 const scopedProject = snapshot?.scopedProject ?? null

 return (
 <div className="flex flex-col">
 <div className="space-y-6">

 {error && (
 <Alert variant="destructive">
 <AlertTriangle className="h-4 w-4" />
 <AlertDescription>Error loading data: {String(error)}</AlertDescription>
 </Alert>
 )}

 {isLoading && (
 <div className="space-y-4">
 {/* Tool ranking (stat band + graph) */}
 <Skeleton className="h-80 rounded-lg" />
 {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-48 rounded-lg" />)}
 </div>
 )}

 {data && (() => {
 const heroStats: StatStripItem[] = [
 {
 title: "Tool Calls",
 value: data.total_tool_calls.toLocaleString(),
 description: "Total all time",
 },
 {
 title: "Unique Tools",
 value: data.tools.length.toLocaleString(),
 description: "Distinct tools used",
 },
 {
 title: "MCP Servers",
 value: data.mcp_servers.length.toLocaleString(),
 description: "Connected servers",
 },
 {
 title: "Errors",
 value: data.total_errors.toLocaleString(),
 description: data.total_tool_calls > 0
 ? `${((data.total_errors / data.total_tool_calls) * 100).toFixed(1)}% error rate`
 : 'No errors',
 },
 ]
 return (
 <>
 {/* Tool ranking — hero card: title chrome + the tool-usage totals banded
     across the head, then the ranking graph. */}
 <Card className="min-w-0 gap-0 overflow-hidden py-0">
 <SectionTitle
 variant="card"
 title="Tool Ranking"
 subtitle={scopedProject ? `Scoped to ${scopedProject.name}` : undefined}
 />
 <StatStrip items={heroStats} variant="table" />
 <CardContent className="py-4">
 {/* Category legend */}
 <div className="mb-3 flex flex-wrap gap-2">
 {Object.entries(CATEGORY_COLORS).map(([cat, color]) => (
 <Badge key={cat} variant="outline" className="gap-1.5">
 <span className="w-2 h-2 rounded-lg shrink-0" style={{ backgroundColor: color }} />
 {CATEGORY_LABELS[cat as keyof typeof CATEGORY_LABELS]}
 </Badge>
 ))}
 </div>
 <ToolRankingChart tools={data.tools} />
 </CardContent>
 </Card>

 {/* MCP server details */}
 {data.mcp_servers.length > 0 && (
 <Card className="min-w-0 gap-0 overflow-hidden py-0">
 <SectionTitle variant="card" title="MCP Server Details" />
 <CardContent className="py-4">
 <McpServerPanel servers={data.mcp_servers} />
 </CardContent>
 </Card>
 )}

 {/* Feature adoption */}
 <FeatureAdoptionTable
 title="Feature Adoption"
 adoption={data.feature_adoption}
 totalSessions={(() => {
 const first = Object.values(data.feature_adoption ?? {})[0]
 return first ? Math.round(first.sessions / Math.max(0.001, first.pct)) : 0
 })()}
 />

 {/* Error analysis */}
 {data.total_errors > 0 && (
 <Card className="min-w-0 gap-0 overflow-hidden py-0">
 <SectionTitle variant="card" title="Tool Error Analysis" />
 <CardContent className="py-4">
 <div className="space-y-2.5">
 {Object.entries(data.error_categories).sort(([, a], [, b]) => b - a).map(([cat, count]) => {
 const max = Math.max(...Object.values(data.error_categories))
 const width = Math.max(4, Math.round((count / max) * 100))
 return (
 <div key={cat} className="flex items-center gap-3">
 <span className="type-body w-36 truncate">{cat}</span>
 <div className="flex-1 h-2 bg-muted rounded-lg overflow-hidden">
 <div className="h-full rounded-lg bg-muted" style={{ width: `${width}%` }} />
 </div>
 <span className="type-body tabular-nums w-8 text-right">{count.toLocaleString()}</span>
 </div>
 )
 })}
 </div>
 </CardContent>
 </Card>
 )}

 {/* Version history */}
 {data.versions.length > 0 && (
 <VersionHistoryTable title="Claude Code Version History" versions={data.versions} />
 )}

 {/* Git branch analytics */}
 {data.branches.length > 0 && (
 <Card className="min-w-0 gap-0 overflow-hidden py-0">
 <SectionTitle variant="card" title="Git Branch Analytics" />
 <CardContent className="py-4">
 <div className="space-y-2">
 {data.branches.map(({ branch, turns }) => {
 const max = data.branches[0]?.turns ?? 1
 const width = Math.max(4, Math.round((turns / max) * 100))
 return (
 <div key={branch} className="flex items-center gap-3">
 <span className="type-body w-28 truncate ">{branch}</span>
 <div className="flex-1 h-2 bg-muted rounded-lg overflow-hidden">
 <div className="h-full rounded-lg bg-[var(--chart-1)]/50" style={{ width: `${width}%` }} />
 </div>
 <span className="type-body tabular-nums w-24 text-right">{turns.toLocaleString()} turns</span>
 </div>
 )
 })}
 </div>
 </CardContent>
 </Card>
 )}
 </>
 )
 })()}
 </div>
 </div>
 )
}
